import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  Renderer2,
  OnDestroy,
  ChangeDetectionStrategy,
  ViewEncapsulation,
  Input,
  AfterViewInit
} from '@angular/core';
import { Select, Store } from '@ngxs/store';
import { EngineState } from 'src/app/engine/state/engine.state';
import { Observable, Subject } from 'rxjs';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';
import { TimerangeModel } from 'src/app/engine/models/animation/timerange.model';
import { EngineService } from 'src/app/engine/engine.service';
import { AnimationService } from 'src/app/engine/animation.service';
import { ViewService } from '../../../view.service';
import { filter, takeUntil, throttleTime, auditTime } from 'rxjs/operators';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { CdkDrag, CdkDragMove } from '@angular/cdk/drag-drop';
import { ViewState } from '../../../state/view.state';
import { ToggleAnimation } from 'src/app/engine/state/engine.actions';

@Component({
  selector: 'app-view-scrubber',
  templateUrl: './view-scrubber.component.html',
  styleUrls: ['./view-scrubber.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class ViewScrubberComponent implements OnInit, OnDestroy, AfterViewInit {
  // ---------- Child Elements ----------
  @ViewChild('scrubber', { static: true})
  scrubber: ElementRef;

  @ViewChild('track', { static: true})
  track: ElementRef;

  @ViewChild('bar', { static: true})
  bar: ElementRef;

  @ViewChild('handle', { static: true})
  handle: ElementRef;

  @ViewChild(CdkDrag)
  dragInstance: CdkDrag;



  // ---------- Observables ----------
  @Select(EngineState.getAnimationPlaying)
  animationPlaying$: Observable<boolean>;

  @Select(StoryChaptersState.getSelectedChapterId)
  selectedChapterId$: Observable<string>;

  @Select(ViewState.getCanTouch)
  canTouch$: Observable<boolean>;


  // ---------- Inputs ----------
  @Input()
  showHandle = true;



  // ---------- Class Variables ----------
  ngUnsubscribe = new Subject();
  timerange: TimerangeModel;
  trackWidth = 0;
  dragPosition = {x: 16, y: 0};
  playing = true;



  // ---------- Lifecycle Hooks ----------
  constructor(
    private engineService: EngineService,
    private animationService: AnimationService,
    private renderer: Renderer2,
    private viewService: ViewService,
    private changeDetectorRef: ChangeDetectorRef,
    private store: Store
  ) { }

  ngOnInit() {
    this.initChapterListener();
    this.initTimelineListener(); 
  }

  ngAfterViewInit() {
    this.initDragListener();
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }



  // ---------- Listeners ----------
  initChapterListener() {
    this.selectedChapterId$.pipe(
      filter(chapterId => chapterId != null),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((chapterId) => {
      this.timerange = this.animationService.getChapterLocalTimerange(chapterId);
    });
  }

  initTimelineListener() {
    // Listen to timeline ticks
    this.engineService.getEvent(EventsEnum.TimelinePlaying)
      .pipe(
        throttleTime(20),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(event => this.tick(event.time));

    // listen for timeline scrub
    this.engineService.getEvent(EventsEnum.TimelineScrubbed)
      .pipe(
        throttleTime(20),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((event) => {
        // manually set scrubber UI
        const scrubPercent = this.calcScrubbedPercent(event.time);
        this.setScrubberBar(scrubPercent);
      });
  }

  initDragListener() {
    this.dragInstance.moved
      .pipe(
        auditTime(20),
        takeUntil(this.ngUnsubscribe)
      ).subscribe((event: CdkDragMove) => {
        const percent = this.calculateHandlePercent();
        this.scrubToPercent(percent);
      });
  }




  // ---------- Class Methods ----------
  handleDragStart() {
    // Store a local copy because we're about to pause animation while draggin
    // to prevent glitches between animation and dragging
    this.playing = this.store.selectSnapshot(EngineState.getAnimationPlaying);
    this.store.dispatch(new ToggleAnimation(false));
  }
  
  handleDragEnd() {
    this.store.dispatch(new ToggleAnimation(this.playing));
  }

  handleTrackClick(event: MouseEvent) {
    // Don't interact when no handle is visible
    if (!this.showHandle) {
      return;
    }

    const percent = ((event.offsetX) / this.track.nativeElement.getBoundingClientRect().width) * 100;
    this.playing = this.store.selectSnapshot(EngineState.getAnimationPlaying);

    this.scrubToPercent(percent);
    this.setScrubberBar(percent);
    this.setScrubberHandle(percent); 

    this.store.dispatch(new ToggleAnimation(this.playing));
  }


  tick(time: number) {
    if (!this.timerange) {
      return;
    }

    const percent = this.calcScrubbedPercent(time);
    this.setScrubberBar(percent);
    this.setScrubberHandle(percent); 
  }

  calculateHandlePercent() {
    const { left, right } = this.scrubber.nativeElement.getBoundingClientRect();
    const offsetWidth = this.handle.nativeElement.offsetWidth;
    const adjustedWidth = right - left - offsetWidth; // Account for padding
    const handleLeft = this.handle.nativeElement.getBoundingClientRect().left;
   
    const percent = (handleLeft - left) / adjustedWidth * 100;
    const normalizedPercent = Math.min(Math.max(percent, 0), 100); // clamp value to range: 0 - 100
    return normalizedPercent;
  }

  scrubToPercent(percent: number) {
    const localTime = (percent / 100) * this.timerange.lastTime;

    const which = (percent === 0) ? 'second' : 'first';

    const activeChapterId = this.store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
    const globalTime = this.animationService.mapLocalTimeToGlobal(activeChapterId, localTime);
    
    this.animationService.scrub({time: globalTime, which});
  }

  handlePlayClick() {
    this.viewService.toggleAnimation();
  }

  private setScrubberBar(percent: number) {
    this.renderer.setStyle(this.bar.nativeElement, 'width', `${percent}%`);
  }

  private setScrubberHandle(percent: number) {
    if (this.trackWidth === 0) {
      this.trackWidth = this.track.nativeElement.offsetWidth;
    }

    if (this.showHandle) {
      this.dragPosition = {
        ...this.dragPosition,
        x: 16 + this.trackWidth * (percent / 100)
      };

      this.changeDetectorRef.markForCheck();
    }
  }

  private calcScrubbedPercent(time: number): number {
    const { firstTime, lastTime } = this.timerange;
    const localTime = this.animationService.mapGlobalTimeToLocal(time).time;

    // keep time in range
    const normalizedTime = Math.max(firstTime, Math.min(localTime, lastTime));
    const scrubPercent = Math.round((normalizedTime / lastTime) * 100);

    return scrubPercent;
  }
}
